// Stale-While-Revalidate Helper fuer die Summary-Stores.
//
// Ziel: Beim Page-Reload bzw. PWA-Open soll die UI sofort die letzten
// bekannten Aggregat-Werte zeigen (z.B. "12 Container running") statt
// erst "lade..." anzuzeigen waehrend das Backend antwortet. Der erste
// Fetch laeuft im Hintergrund parallel; sobald frische Daten da sind,
// ersetzen sie den gecachten Snapshot.
//
// Pattern fuer Stores:
//   import { loadPersisted, persist } from "./persistent-summary.svelte";
//
//   const KEY = "network";
//   const cached = loadPersisted<NetworkSummary>(KEY);
//   if (cached) _summary.set("current", cached);
//   ...
//   async function refresh() {
//     ...
//     const next = { ... };
//     _summary.set("current", next);
//     persist(KEY, next);
//   }
//
// Speicherort: localStorage, Prefix `hl:sum:` damit Settings/Theme
// etc. nicht versehentlich ueberschrieben werden. Quota-Probleme sind
// best-effort -- bei Fehler einfach kein Persist (fallback auf das
// alte Verhalten "lade..." beim Cold-Start).
//
// TTL: 7 Tage. Wer eine Woche offline war, sieht beim Re-Open
// "lade..." statt veraltete Zahlen -- das ist okay. Sub-Minuten-
// frisches Cache-Bild stoert nicht, aber ein 2-Wochen-altes
// "12 Pools belegt 78%" ist irritierend.

const PREFIX = "hl:sum:";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Stored<T> {
  data: T;
  ts: number;
}

export function loadPersisted<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Stored<T>;
    if (!parsed || typeof parsed.ts !== "number") return undefined;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      localStorage.removeItem(PREFIX + key);
      return undefined;
    }
    return parsed.data;
  } catch {
    return undefined;
  }
}

export function persist<T>(key: string, data: T): void {
  try {
    const payload: Stored<T> = { data, ts: Date.now() };
    localStorage.setItem(PREFIX + key, JSON.stringify(payload));
  } catch {
    // Quota voll oder Privacy-Mode -- best effort
  }
}

export function clearPersisted(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    /* ignore */
  }
}
